/**
 * The desktop's chat with the AI on this machine — an assistant panel like an IDE's, without MCP.
 * See lib/localChat.ts for how turns run and how the AI drives Vephus through actions.
 *
 * The conversation lives in this window (one per repository), and each turn runs the person's own
 * agent in the repository's folder with their own subscription. An action the AI proposes is a
 * card; nothing happens until the person runs it, and its result goes back to the AI.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bot, Check, Loader2, Play, RotateCcw, Send, Square, Wrench, X } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { usePromptCatalog, runPrompt } from '../lib/prompts'
import { folderFor } from '../lib/repoFolders'
import { folderBusy, readRunnerSettings, runExclusive } from '../lib/runnerHost'
import { agentProgram, AGENT_NAMES, runAgent } from '../lib/localAgent'
import { chatTurnReport, instructionsFor } from '../lib/runnerHost'
import { extractActions, firstTurn, parseOpenCodeLine, parseStreamLine, runAction, type ChatAction } from '../lib/localChat'
import { repoBranches } from '../lib/desktop'
import type { RepositoryLink } from '../lib/types'
import { ApiChat } from './ApiChat'
import { useRepoFiles } from '../lib/repoSource'

type Entry =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'assistant'; text: string; streaming: boolean }
  | { id: string; role: 'tool'; name: string; detail: string }
  | { id: string; role: 'action'; action: ChatAction; state: 'proposed' | 'running' | 'done' | 'skipped' | 'failed'; result?: string }
  | { id: string; role: 'note'; text: string; bad?: boolean }

interface Conversation {
  sessionId: string
  started: boolean
  /** OpenCode's own session id (ses_…), read from the first turn's events and resumed after (SDD task 5). */
  agentSessionId?: string
  entries: Entry[]
}

/** One conversation per repository, kept while the window is open. */
const conversations = new Map<string, Conversation>()

function conversationFor(repositoryId: string): Conversation {
  let conversation = conversations.get(repositoryId)
  if (!conversation) {
    conversation = { sessionId: crypto.randomUUID(), started: false, entries: [] }
    conversations.set(repositoryId, conversation)
  }
  return conversation
}

const id = () => crypto.randomUUID()

function CliChat({ repository }: { repository: RepositoryLink | null }) {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const [conversation, setConversation] = useState<Conversation | null>(() => (repository ? conversationFor(repository.id) : null))
  const [text, setText] = useState('')
  const [running, setRunning] = useState(false)
  const stopRef = useRef<(() => Promise<void>) | null>(null)
  const bottom = useRef<HTMLDivElement>(null)
  const { data: catalog } = usePromptCatalog()
  const { data: teammates } = useQuery({ queryKey: ['teammates', repository?.id ?? null], queryFn: () => api.workOrders.teammates(repository?.id), retry: false })

  const folder = repository ? folderFor(repository.name) : null
  const settings = readRunnerSettings()
  const template = useMemo(() => catalog?.prompts.find((p) => p.key === 'tool.desktop-chat')?.body ?? null, [catalog])

  useEffect(() => setConversation(repository ? conversationFor(repository.id) : null), [repository])
  // Braces: in recent WebView2 builds scrollIntoView returns a Promise, which React would take as the clean-up.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [conversation])

  const update = (change: (c: Conversation) => void) => {
    if (!repository) return
    const current = conversationFor(repository.id)
    change(current)
    const copy = { ...current, entries: [...current.entries] }
    conversations.set(repository.id, copy)
    setConversation(copy)
  }

  /** One turn: runs the agent in the conversation's session and streams what it says. */
  const turn = async (message: string, shown: string | null) => {
    if (!repository || !folder) return
    const current = conversationFor(repository.id)
    let prompt = message
    if (!current.started) {
      const branch = await repoBranches(folder).then((b) => b.current).catch(() => null)
      prompt = template ? firstTurn(template, { repository: repository.name, branch, teammates: teammates ?? [] }, message) : message
    }
    const answerId = id()
    update((c) => {
      if (shown !== null) c.entries.push({ id: id(), role: 'user', text: shown })
      c.entries.push({ id: answerId, role: 'assistant', text: '', streaming: true })
    })
    setRunning(true)

    const claude = agentProgram(settings.agent) === 'claude'
    // Which reader this agent's output takes (IA local SDD, task 4): Claude's stream-json, OpenCode's
    // JSON events, or none — plain text appended as it comes (Codex).
    const parse = claude ? parseStreamLine : settings.agent === 'opencode' ? parseOpenCodeLine : null
    // Tokens accumulate across the turn: Claude reports once at the end; OpenCode once per step.
    const turnUsage = { input: 0, output: 0, cached: 0 }
    let raw = ''
    const append = (chunk: string) => {
      raw += chunk
      const { text: visible } = extractActions(raw)
      update((c) => {
        const entry = c.entries.find((e) => e.id === answerId)
        if (entry && entry.role === 'assistant') entry.text = visible
      })
    }

    // Reported as a prompt run here is (docs/refactor/01-rodar-aqui.md): the agent's hooks stay quiet.
    const report = repository ? chatTurnReport(repository.name, current.sessionId) : null
    void report?.started(prompt, null)
    // One agent per folder: a queued prompt or remote command running here goes first.
    if (folderBusy(folder)) update((c) => c.entries.push({ id: id(), role: 'note', text: t('chat.waitingFolder') }))
    try {
      // The project's and this machine's instructions and the skills, with every turn (docs/refactor/21).
      const system = await instructionsFor(folder, true).catch(() => '')
      await runExclusive(folder, () => new Promise<void>((finished, failed) => {
      runAgent(
        crypto.randomUUID(),
        settings.agent,
        folder,
        system && !claude ? `${system}\n\n---\n\n${prompt}` : prompt,
        settings.permission,
        {
          onLine: (line, stream) => {
            if (!parse) {
              if (stream === 'stdout') append(`${line}\n`)
              return
            }
            for (const event of parse(line)) {
              if (event.kind === 'init') report?.model(event.model)
              // OpenCode names its own session on the first event; the next turn resumes it.
              if (event.kind === 'init' && event.sessionId?.startsWith('ses_')) {
                const adopted = event.sessionId
                update((c) => {
                  c.agentSessionId = adopted
                })
              }
              if (event.kind === 'result' && event.usage) {
                turnUsage.input += event.usage.input
                turnUsage.output += event.usage.output
                turnUsage.cached += event.usage.cached
                report?.usage({ ...turnUsage })
              }
              if (event.kind === 'tool') report?.tool(event.name, event.detail, event.command)
              if (event.kind === 'text') report?.said(event.text)
              if (event.kind === 'text') append(raw ? `\n\n${event.text}` : event.text)
              if (event.kind === 'tool') update((c) => c.entries.splice(c.entries.findIndex((e) => e.id === answerId), 0, { id: id(), role: 'tool', name: event.name, detail: event.detail }))
              if (event.kind === 'result' && event.isError) update((c) => c.entries.push({ id: id(), role: 'note', text: event.text || t('chat.failed'), bad: true }))
            }
          },
          onExit: (code) => {
            stopRef.current = null
            setRunning(false)
            const { actions, text: said } = extractActions(raw)
            void report?.ended(said, [])
            update((c) => {
              c.started = true
              const entry = c.entries.find((e) => e.id === answerId)
              if (entry && entry.role === 'assistant') {
                entry.streaming = false
                if (!entry.text.trim()) entry.text = code === 0 ? t('chat.noText') : t('chat.failed')
              }
              for (const action of actions) c.entries.push({ id: id(), role: 'action', action, state: 'proposed' })
            })
            finished()
          },
        },
        claude ? { sessionId: current.sessionId, resume: current.started }
          : settings.agent === 'opencode' ? { sessionId: current.agentSessionId ?? current.sessionId, resume: current.started && Boolean(current.agentSessionId) }
          : undefined,
        { reportedByApp: Boolean(report), system: claude ? system : undefined, model: settings.models?.[settings.agent] },
      )
        .then((stop) => {
          stopRef.current = stop
        })
        .catch(failed)
      }))
    } catch (error) {
      setRunning(false)
      const code = String(error)
      update((c) => c.entries.push({ id: id(), role: 'note', text: code.includes('not-installed') ? t('chat.notInstalled') : code.includes('folder-not-picked') ? t('chat.noFolder') : code, bad: true }))
    }
  }

  const send = () => {
    const message = text.trim()
    if (!message || running) return
    setText('')
    void turn(message, message)
  }

  /** Runs a proposed action, then tells the AI what happened, as the next turn. */
  const execute = async (entryId: string, action: ChatAction) => {
    update((c) => {
      const entry = c.entries.find((e) => e.id === entryId)
      if (entry && entry.role === 'action') entry.state = 'running'
    })
    let result: string
    let ok = true
    try {
      result = await runAction(action, {
        repositoryId: repository?.id ?? null,
        navigate,
        runAudit: async (key) => {
          const prompt = catalog?.prompts.find((p) => p.key === key)
          if (!prompt) return `No audit called ${key}.`
          const order = await runPrompt(prompt, repository?.id ?? null, locale)
          return `Audit ${key} started (work order ${order.id}).`
        },
      })
    } catch (error) {
      ok = false
      result = error instanceof ApiError ? `${error.code ?? error.status}: ${error.message}` : String(error)
    }
    update((c) => {
      const entry = c.entries.find((e) => e.id === entryId)
      if (entry && entry.role === 'action') {
        entry.state = ok ? 'done' : 'failed'
        entry.result = result
      }
    })
    if (action.type !== 'open') void turn(`[Vephus] ${ok ? 'Action done' : 'Action failed'} (${action.type}): ${result}`, null)
  }

  const skip = (entryId: string) =>
    update((c) => {
      const entry = c.entries.find((e) => e.id === entryId)
      if (entry && entry.role === 'action') entry.state = 'skipped'
    })

  if (!repository) return <p className="small muted" style={{ margin: 0 }}>{t('chat.noRepository')}</p>
  if (!folder) return <p className="small muted" style={{ margin: 0 }}>{t('chat.noFolderHere', { name: repository.name })}</p>

  return (
    <div className="local-chat">
      <div className="local-chat__log" aria-live="polite">
        {(conversation?.entries ?? []).length === 0 && (
          <div className="local-chat__empty small muted">
            <Bot size={16} /> {t('chat.empty', { name: repository.name, agent: AGENT_NAMES[settings.agent] ?? settings.agent })}
          </div>
        )}
        {(conversation?.entries ?? []).map((entry) => {
          if (entry.role === 'user') return <div key={entry.id} className="local-chat__msg is-user">{entry.text}</div>
          if (entry.role === 'assistant')
            return (
              <div key={entry.id} className="local-chat__msg is-ai">
                {entry.text || (entry.streaming ? <span className="muted"><Loader2 size={12} className="spin" /> {t('chat.thinking')}</span> : null)}
              </div>
            )
          if (entry.role === 'tool')
            return (
              <div key={entry.id} className="local-chat__tool tiny mono">
                <Wrench size={11} /> {entry.name} {entry.detail && <span className="muted">· {entry.detail}</span>}
              </div>
            )
          if (entry.role === 'note') return <div key={entry.id} className={`local-chat__note tiny${entry.bad ? ' is-bad' : ''}`}>{entry.text}</div>
          return (
            <div key={entry.id} className={`local-chat__action is-${entry.state}`}>
              <div className="small"><strong>{t(`chat.action.${entry.action.type}`)}</strong> <span className="muted">{describe(entry.action)}</span></div>
              {entry.result && <div className="tiny muted">{entry.result}</div>}
              {entry.state === 'proposed' && (
                <div className="row row--tight">
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => void execute(entry.id, entry.action)}><Play size={12} /> {t('chat.run')}</button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => skip(entry.id)}><X size={12} /> {t('chat.skip')}</button>
                </div>
              )}
              {entry.state === 'running' && <span className="tiny muted"><Loader2 size={12} className="spin" /> {t('chat.running')}</span>}
              {entry.state === 'done' && <span className="tiny ok"><Check size={12} /> {t('chat.done')}</span>}
            </div>
          )
        })}
        <div ref={bottom} />
      </div>

      <div className="local-chat__compose">
        <textarea
          className="input"
          rows={2}
          value={text}
          placeholder={t('chat.placeholder')}
          aria-label={t('chat.placeholder')}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault()
              send()
            }
          }}
        />
        <div className="row row--tight">
          {running ? (
            <button type="button" className="btn btn--sm" onClick={() => void stopRef.current?.()}><Square size={12} /> {t('chat.stop')}</button>
          ) : (
            <button type="button" className="btn btn--primary btn--sm" onClick={send} disabled={!text.trim()}><Send size={12} /> {t('dock.send')}</button>
          )}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            title={t('chat.new')}
            disabled={running}
            onClick={() => {
              conversations.delete(repository.id)
              setConversation(conversationFor(repository.id))
            }}
          >
            <RotateCcw size={12} /> {t('chat.new')}
          </button>
        </div>
      </div>
    </div>
  )
}

function describe(action: ChatAction): string {
  switch (action.type) {
    case 'work_order':
    case 'queue':
    case 'team':
      return action.instructions.length > 140 ? `${action.instructions.slice(0, 139)}…` : action.instructions
    case 'doc':
    case 'pin':
      return action.path
    case 'audit':
      return action.key
    case 'open':
      return action.route
  }
}

const ENGINE_KEY = 'devpulse.chat.engine'

/**
 * The desktop chat, with its two engines: the agent on this machine (Claude Code / Codex CLI, which
 * works on the files itself and drives Vephus through action cards) or an AI provider's official API
 * with the person's own key (which drives Vephus through tools). The choice is remembered.
 */
export function LocalChat({ repository }: { repository: RepositoryLink | null }) {
  const { t } = useI18n()
  const [engine, setEngine] = useState<'cli' | 'api'>(() => {
    try {
      return localStorage.getItem(ENGINE_KEY) === 'api' ? 'api' : 'cli'
    } catch {
      return 'cli'
    }
  })
  const { data: view } = useRepoFiles(repository)
  const choose = (next: 'cli' | 'api') => {
    setEngine(next)
    try {
      localStorage.setItem(ENGINE_KEY, next)
    } catch {
      // Private storage: the choice lasts until the window closes.
    }
  }
  return (
    <div className="local-chat">
      <div className="segmented segmented--sm" role="radiogroup" aria-label={t('chat.engine')}>
        {(['cli', 'api'] as const).map((e) => (
          <button key={e} type="button" role="radio" aria-checked={engine === e} className={`segmented__item${engine === e ? ' segmented__item--active' : ''}`} onClick={() => choose(e)}>
            {t(`chat.engine.${e}`)}
          </button>
        ))}
      </div>
      {engine === 'api' && repository ? (
        <ApiChat repository={repository} folder={folderFor(repository.name)} branch={view?.branch ?? null} />
      ) : (
        <CliChat repository={repository} />
      )}
    </div>
  )
}
